/**
 * Field constraints of the `ctg_grantapplication` Dataverse entity.
 *
 * These numbers are not guesses - they were read from the live org metadata:
 *
 *   GET {org}/api/data/v9.2/EntityDefinitions(LogicalName='ctg_grantapplication')/Attributes
 *         /Microsoft.Dynamics.CRM.StringAttributeMetadata?$select=LogicalName,MaxLength
 *         /Microsoft.Dynamics.CRM.MemoAttributeMetadata?$select=LogicalName,MaxLength
 *         /Microsoft.Dynamics.CRM.IntegerAttributeMetadata?$select=LogicalName,MinValue,MaxValue
 *
 * Org: org14252cc0.crm4.dynamics.com - retrieved 2026-08-31.
 * Re-run those queries and update this file after any CRM schema change; every form that writes to
 * the entity reads its limits from here, so there are no magic numbers in the components.
 */
export const GRANT_APPLICATION_LIMITS = {
  /**
   * ctg_title. The Dataverse column allows 850; 60 is an NHC business rule applied to every
   * application form, because titles appear in listings and donor reporting. Introduced for the
   * basic form in commit 991dce1 and extended to the emergency forms afterwards - the two forms
   * write to the same column and must not diverge.
   */
  applicationName: 60,
  /** ctg_location (string) */
  location: 100,
  /** ctg_applicationdescription (memo) */
  applicationDescription: 4000,
  /** ctg_ongoingprojects (memo) */
  onGoingProject: 4000,
  /** ctg_cofundingdetails (memo) */
  cofundDescription: 2000,
  /** ctg_emergency_purpose (memo) */
  fundingPupose: 2000,
  /** ctg_emergencycostexplanation (memo) */
  costExplanation: 2000,
} as const;

/** ctg_amount (integer) */
export const GRANT_APPLICATION_AMOUNT = {
  min: 0,
  max: 2147483647,
} as const;

/**
 * Column constraints of the ctg_report entity (the Application Report page).
 *
 * Read from the live org metadata on 2026-09-02, same queries as above but with
 * LogicalName='ctg_report'.
 *
 * Note ctg_report has NO memo columns - every narrative answer is a single line of text with a
 * small limit, even though the page renders them as multi-line textareas.
 *
 * Unlike the grant application forms, this page posts JSON (http.post with a plain object, bound
 * with [FromBody]), so line breaks are NOT normalised to CRLF: a line break costs one character
 * here. Use plain Validators.maxLength, and pass [lineBreakCost]="1" to app-char-counter.
 */
export const REPORT_LIMITS = {
  /** ctg_cofundingdetails (string) - note: the same name on ctg_grantapplication is a memo of 2000 */
  cofundingDescription: 300,
  /** ctg_projectimplementation (string) */
  projectImplementDescription: 300,
  /** ctg_otherpartnerships (string) */
  otherPartnerrAndNetwork: 300,
  /** ctg_crosscuttingissues (string) */
  crossCuttingIssue: 300,
  /** ctg_whatstory (string) */
  whatStory: 500,
} as const;

/**
 * Whole-number range for the report amounts and target group counts:
 * ctg_transferred_amount, ctg_spent_amount, ctg_budget_next_period,
 * ctg_num_male / ctg_num_female / ctg_num_otherindividuals, ctg_impactedindividualstotal.
 *
 * CRM allows negatives on ctg_budget_next_period and the ctg_num_* columns; the portal deliberately
 * keeps min 0 for all of them, which is stricter than the platform.
 */
export const REPORT_AMOUNT = {
  min: 0,
  max: 2147483647,
} as const;

/**
 * Whole-number ranges on the account entity, used by the Organization page and the Registration
 * form. Both write the same columns, so both must enforce the same bounds.
 *
 * Read from the live org metadata on 2026-09-02 with LogicalName='account'. All three are Integer
 * columns, so a decimal is not just out of range - the API binds them as `int?` and a value like
 * "1.5" fails model binding, which [ApiController] turns into an opaque 400 before the action even
 * runs. Always pair these with wholeNumberValidator().
 */
export const ACCOUNT_LIMITS = {
  /** ctg_annualbudget (integer) */
  annualBudget: { min: 0, max: 2147483647 },
  /** numberofemployees (integer) */
  numberOfEmployees: { min: 0, max: 1000000000 },
  /** ctg_numberofvoulenteers (integer) */
  numberOfVoulenteers: { min: 0, max: 1000000000 },
} as const;

/**
 * Text column limits on the account entity, written by both the Registration form and the
 * Organization page. Read from the live org metadata on 2026-09-02 with LogicalName='account'.
 *
 * Watch the transport when applying these - the two pages differ:
 *   - Organization page posts JSON (organizationForm.value -> [FromBody]), so a line break costs
 *     one character: use Validators.maxLength and the native maxlength attribute is exact.
 *   - Registration form posts multipart/form-data (new FormData() -> [FromForm]), so line breaks
 *     are rewritten to CRLF: its one textarea (description) needs storedMaxLengthValidator.
 */
export const ACCOUNT_TEXT_LIMITS = {
  /** address1_line1 (string) */
  addressStreet: 500,
  /** address1_city (string) */
  addressCity: 80,
  /** address1_postalcode (string) */
  addressPostal: 20,
  /** telephone1 (string) */
  phone: 50,
  /** websiteurl (string) */
  webSiteUrl: 200,
  /** description (memo) - rendered as a textarea on both pages */
  description: 300,
  /** ctg_references (string) - single line of text in CRM, but rendered as a textarea */
  references: 100,
} as const;
